import { Injectable, Component, OnDestroy, Inject, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpEventType, HttpResponse, HttpHeaderResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { ConfigurationService } from '../configuration/configuration.service';

import { OidcSecurityService, OpenIDImplicitFlowConfiguration, AuthWellKnownEndpoints, OidcConfigService, OidcSecurityValidation, TokenHelperService, ValidationResult } from 'angular-auth-oidc-client';
import { LocalStorageService } from '../common/local.storage.service/local.storage.service';
import { Subject } from 'rxjs/Subject';
import CustomUriEncoder from '../common/custom.uri.encoder';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { CacheTypes } from '../common/service.base';
import { OidcSecurityCommon } from 'angular-auth-oidc-client/lib/services/oidc.security.common';
import * as _ from 'underscore';
import * as $ from 'jquery';

@Injectable()
export class AuthService implements OnDestroy {

  public static unauthorizedReturnUrl = 'unauthorizedReturnUrl';
  private userDataSubscription: Subscription;
  private postPayloads = Array<any>();
  private putPayloads = Array<any>();
  private openIDImplicitFlowConfiguration;
  private authWellKnownEndpoints;

  public uploadProgress: any;

  public user: any;
  public notificationChanged = new EventEmitter<string>();
  public tokenRefreshed = new EventEmitter<any>();
  public userRefreshed = new EventEmitter<boolean>();
  public logoutInProgress = false;
  public authorizationValidationResult: ValidationResult = ValidationResult.NotSet;

  get isAuthorized(): boolean {
    const token = this.oidcSecurityService.getToken();

    if (token) {
      if (this.oidcSecurityValidation.isTokenExpired(token)) {
        return false;
      }
    } else { return false; }

    return true;
  }

  get tokenExpirationInSeconds(): number {
    const token = this.oidcSecurityService.getToken();

    if (token) {
      const data = this.tokenHelperService.getPayloadFromToken(token, false);
      const tokenExpirationDate = this.tokenHelperService.getTokenExpirationDate(data);
      const expiresIn = (tokenExpirationDate.valueOf() - new Date().valueOf())/1000;
      return (expiresIn > 0) ? expiresIn : 0;
    } 

    return 0;
  }

  hasAccessByRoute(route: ActivatedRouteSnapshot): boolean {
    if (!this.user && route.data && route.data.auth) { return false; }

    if (!route.data || !route.data.auth) { return true; }

    const roleType = route.data.auth.roleType;
    const claim = route.data.auth.claim;
    const anyclaims = claim ? claim.any : undefined;

    let access = (!roleType || this.user.role_type === roleType);
    if (this.user.role && anyclaims) {
      let hasClaim = false;

      anyclaims.forEach(claim => {
        hasClaim = hasClaim || this.user.role.includes(claim);
      });
      
      access = access && hasClaim;
    }

    if (!anyclaims) {
      access = access && (!claim || (this.user.role && this.user.role.includes(claim)));
    }
    
    return access;
  }

  hasAccess(roleType: string, claim: any): boolean {
    if (!this.user) { return false; }
    if (roleType && this.user.role_type !== roleType) { return false; }

    let access = false;
    if (typeof claim === 'object' && claim !== null) {
      if (claim.any) {
        for (let i = 0; i < claim.any.length; i++) {
          access =  (!claim.any[i] || this.user.role.includes(claim.any[i]));

          if (access) { break; }
        }
      } else if (claim.all) {
        for (let i = 0; i < claim.all.length; i++) {
          access =  (!claim.all[i] || this.user.role.includes(claim.all[i]));

          if (!access) { break; }
        }
      }
    } else {
      access = (!claim || this.user.role.includes(claim));
    }
    return access;
  }

  hasScope(scope: string): boolean {
    if (!this.user) { return false; }

    return !!this.user[scope];
  }

  hasBusinessRole(businessRole: string): boolean {
    return this.hasBusinessRule(businessRole);
  }

  //Obsolete
  hasBusinessRule(businessRule: string): boolean {
    if (!this.user || !this.user.business_role || !businessRule) { return false; }

    return this.user.business_role == businessRule || this.user.business_role.includes(businessRule);
  }

  get returnUrl(): string { return this.localStorage.get(AuthService.unauthorizedReturnUrl); }

  constructor(
    public oidcSecurityService: OidcSecurityService,
    private oidcSecurityValidation: OidcSecurityValidation,
    private http: HttpClient,
    @Inject('ORIGIN_URL') originUrl: string,
    configuration: ConfigurationService,
    private localStorage: LocalStorageService,
    private customUriEncoder: CustomUriEncoder,
    private tokenHelperService: TokenHelperService
  ) {
    // NOTE: Space a végeken !!
    const scopes =
      'OrganizationManagement ' +
      'ProfileManagement ' +
      'UserManagement ' +
      'AssignmentManagement ' +
      'CodeListManagement ' +
      'LabelManagement ' +
      'InternalMessageManagement ' +
      'PortalMessageManagement ' +
      'HelpMessageManagement ' +
      'MaintenanceMessageManagement ' +
      'RoleManagement ' +
      'FormManagement ' +
      'DataEntryManagement ' +
      'ChangeManagement ' +
      'WorkflowManagement ' +
      'CaseManagement ' +
      'KPIManagement ' +
      'BulletinManagement ' +
      'FaqMessageManagement ' +
      'ValidationManagement ' +
      'BatchManagement ' +
      'NotificationManagement ' +
      'CustomPropManagement ' +
      'OfficialActManagement ' +
      'InspectionManagement ' +
      'SecGroupManagement ' +
      'UniversalModule ' +
      'BackendForFrontend '
      ;

    this.openIDImplicitFlowConfiguration = new OpenIDImplicitFlowConfiguration();
    this.openIDImplicitFlowConfiguration.stsServer = configuration.identityServerAddress;
    this.openIDImplicitFlowConfiguration.silent_renew = false;
    this.openIDImplicitFlowConfiguration.silent_renew_url = originUrl + 'auth.html';
    this.openIDImplicitFlowConfiguration.redirect_url = originUrl/* + 'auth.html'*/;
    this.openIDImplicitFlowConfiguration.client_id = 'ng';
    this.openIDImplicitFlowConfiguration.response_type = 'id_token token';
    this.openIDImplicitFlowConfiguration.scope = 'openid profile role profile_id mandatory_update mandatory_authenticator default_roles security_group business_role ' + scopes;
    this.openIDImplicitFlowConfiguration.post_logout_redirect_uri = originUrl + 'home';
    this.openIDImplicitFlowConfiguration.forbidden_route = '/forbidden';
    this.openIDImplicitFlowConfiguration.unauthorized_route = '/unauthorized';
    this.openIDImplicitFlowConfiguration.auto_userinfo = true;
    this.openIDImplicitFlowConfiguration.log_console_warning_active = true;
    this.openIDImplicitFlowConfiguration.log_console_debug_active = false;
    this.openIDImplicitFlowConfiguration.max_id_token_iat_offset_allowed_in_seconds = 30;
    this.openIDImplicitFlowConfiguration.post_login_route = this.openIDImplicitFlowConfiguration.post_login_route || this.returnUrl;

    this.authWellKnownEndpoints = new AuthWellKnownEndpoints();

    let issuer = configuration.issuer;
    if (issuer.indexOf(';') >= 0) {
      issuer = issuer.split(';').find(iss => iss.indexOf(originUrl.split('//')[1]) >= 0);
    }

    if (window.location.href.endsWith('?logout')) {
      sessionStorage.clear();
    }

    this.authWellKnownEndpoints.issuer = issuer;
    this.authWellKnownEndpoints.jwks_uri = configuration.identityServerAddress + '/.well-known/openid-configuration/jwks';
    this.authWellKnownEndpoints.authorization_endpoint = configuration.identityServerAddress + '/connect/authorize';
    this.authWellKnownEndpoints.token_endpoint = configuration.identityServerAddress + '/connect/token';
    this.authWellKnownEndpoints.userinfo_endpoint = configuration.identityServerAddress + '/connect/userinfo';
    this.authWellKnownEndpoints.end_session_endpoint = configuration.identityServerAddress + '/connect/endsession';
    this.authWellKnownEndpoints.check_session_iframe = configuration.identityServerAddress + '/connect/checksession';
    this.authWellKnownEndpoints.revocation_endpoint = configuration.identityServerAddress + '/connect/revocation';
    this.authWellKnownEndpoints.introspection_endpoint = configuration.identityServerAddress + '/connect/introspect';
    this.oidcSecurityService.setupModule(this.openIDImplicitFlowConfiguration, this.authWellKnownEndpoints);

    this.oidcSecurityService.onAuthorizationResult.subscribe(({ authorizationState, validationResult }) => {
      this.authorizationValidationResult = validationResult;
    });

    this.userDataSubscription = this.getUser().subscribe(
      (userData: any) => {
        this.user = userData;
        this.userRefreshed.emit(!!this.user);
      });

    if (window.location.hash) {
      this.oidcSecurityService.authorizedImplicitFlowCallback();
    }

    this.uploadProgress = [];
  }

  enableSilentRenew(): void {
    if (!this.openIDImplicitFlowConfiguration.silent_renew) {
      this.openIDImplicitFlowConfiguration.silent_renew = true;
      this.oidcSecurityService.setupModule(this.openIDImplicitFlowConfiguration, this.authWellKnownEndpoints);
    }
  }

  disableSilentRenew(): void {
    if (this.openIDImplicitFlowConfiguration.silent_renew) {
      this.openIDImplicitFlowConfiguration.silent_renew = false;
      this.oidcSecurityService.setupModule(this.openIDImplicitFlowConfiguration, this.authWellKnownEndpoints);
    }
  }

  ngOnDestroy(): void {
    this.userDataSubscription.unsubscribe();
  }

  getIsAuthorized(): Observable<boolean> {
    return this.oidcSecurityService.getIsAuthorized();
  }

  getUser(): Observable<any> {
    return this.oidcSecurityService.getUserData();
  }

  signIn(authCallback: (authUrl: string) => any): void {
    this.oidcSecurityService.authorize((url) => {
      authCallback(url);
    });
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  refreshSession() {
    if (this.isAuthorized) { this.enableSilentRenew(); }

    this.oidcSecurityService.refreshSession();
  }

  logout() {
    this.logoutInProgress = true;
    this.oidcSecurityService.logoff();
  }

  public get<T>(url: string, queryObject?: any, cache = CacheTypes.NoCache): Promise<T> {
    return this.handleObservable<T>(this._get(url, queryObject, cache));
  }

  public post<T>(url: string, payload?: any, cache = CacheTypes.NoCache): Promise<T> {
    if (this.postPayloads.length === 1 && _.isEqual(this.postPayloads[0], payload)) { return this.handleObservable(this.postPayloads[0]); }

    this.postPayloads.push(payload);

    const post = this.handleObservable<T>(this._post(url, payload, cache));

    setTimeout(() => {
      const deletePayloadIndex = this.postPayloads.indexOf(payload);
      this.postPayloads.splice(deletePayloadIndex, 1);
    }, 500);

    return post;
  }

  public put<T>(url: string, payload: any, cache = CacheTypes.NoCache): Promise<T> {
    if (this.putPayloads.length === 1 && _.isEqual(this.putPayloads[0], payload)) { return this.handleObservable(this.putPayloads[0]); }

    this.putPayloads.push(payload);

    const put = this.handleObservable<T>(this._put(url, payload, cache));

    setTimeout(() => {
      const deletePayloadIndex = this.putPayloads.indexOf(payload);
      this.putPayloads.splice(deletePayloadIndex, 1);
    }, 500);

    return put;
  }

  public patch<T>(url: string, payload: any, cache = CacheTypes.NoCache): Promise<T> {
    return this.handleObservable<T>(this._patch(url, payload, cache));
  }

  public delete(url: string, cache = CacheTypes.NoCache): Promise<any> {
    return this.handleObservable(this._delete(url, cache));
  }

  private _get(url: string, params?: any, cache = CacheTypes.NoCache): Observable<any> {
    const _params = new HttpParams({ fromObject: params, encoder: this.customUriEncoder });
    return this.http.get<any>(url, { headers: this.getHeaders(undefined, cache), params: _params });
  }

  private _put(url: string, data: any, cache = CacheTypes.NoCache): Observable<any> {
    return this.http.put<any>(url, data, { headers: this.getHeaders(undefined, cache) });
  }

  private _patch(url: string, data: any, cache = CacheTypes.NoCache): Observable<any> {
    return this.http.patch<any>(url, data, { headers: this.getHeaders(undefined, cache) });
  }

  private _delete(url: string, cache = CacheTypes.NoCache): Observable<any> {
    return this.http.delete<any>(url, { headers: this.getHeaders(undefined, cache) });
  }

  private _post(url: string, data: any, cache = CacheTypes.NoCache): Observable<any> {
    return this.http.post<any>(url, data, { headers: this.getHeaders(undefined, cache) });
  }

  private getHeaders(contentType: string = 'application/json', cache = CacheTypes.NoCache) {
    let headers = new HttpHeaders();
    if (contentType) { headers = headers.set('Content-Type', contentType); }
    if (cache) {
      headers = headers.set('Cache-Control', cache.AsHeaderValue);
    }

    return this.appendAuthHeader(headers);
  }

  private appendAuthHeader(headers: HttpHeaders) {
    const token = this.oidcSecurityService.getToken();

    if (token === '') { return headers; }

    const tokenValue = 'Bearer ' + token;
    return headers.set('Authorization', tokenValue);
  }

  public upload(url: string, files: Set<File>, doneCallback?: (any) => void, errorCallback?: (any) => void): { [key: string]: Observable<number> } {
    const status = {};

    files.forEach(file => {
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);

      const req = new HttpRequest('POST', url, formData, {
        headers: this.getHeaders(''),
        reportProgress: true,
        responseType: "text"
      });

      const progress = new Subject<number>();
      this.http.request(req).subscribe(event => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round((100 * event.loaded) / event.total);
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          progress.complete();
          if (doneCallback) doneCallback(event.body);
        }
      }, e => {
        if (errorCallback) errorCallback(e);
      });

      status[file.name] = {
        progress: progress.asObservable()
      };
    });

    return status;
  }

  public uploadWithPayload(url: string, payload: any, files: Set<File>, doneCallback?: () => void, errorCallback?: (any) => void): { [key: string]: Observable<number> } {
    const status = {};

    files.forEach(file => {
      const formData: FormData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      formData.append('file', file, file.name);

      const req = new HttpRequest('POST', url, formData, {
        headers: this.getHeaders(''),
        reportProgress: true
      });

      const progress = new Subject<number>();
      this.http.request(req).subscribe(event => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round((100 * event.loaded) / event.total);
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          progress.complete();
          if (doneCallback) doneCallback();
        }
      }, e => {
        if (errorCallback) errorCallback(e);
      });

      status[file.name] = {
        progress: progress.asObservable()
      };
    });

    return status;
  }
  
  public uploadExternalFile(url: string, file: File, id: any, reference: any, blobalias: any, doneCallback?: (id: any) => void, errorCallback?: (id: any, reason: any) => void): Observable<number> {
    const progress = new Subject<number>();

    var referenceDetail = reference ? ((typeof reference === 'function') ? reference() : reference ) : "";
    this.uploadExternalFileInternal(url, file, id, referenceDetail, blobalias, progress, 0, doneCallback, errorCallback);

    return progress;
  }

  private uploadExternalFileInternal(url: string, file: File, id: any, reference: any, blobalias: any, progress: Subject<number>, chunk_number: number, doneCallback?: (id: any) => void, errorCallback?: (id: any, reason: any) => void) {
    const chunk_size = 1024 * 1024;
    let offset = chunk_number * chunk_size;

    if (offset < file.size) {
      const chunk = file.slice(offset, offset + chunk_size);
      const chunk_blob = new Blob([chunk], { type: file.type });

      const formData: FormData = new FormData();
      formData.append('file', chunk_blob);
      formData.append('name', file.name);
      formData.append('reference', reference);
      formData.append('alias', blobalias);
      formData.append('chunk_number', String(chunk_number));
      formData.append('total_chunks', String(Math.ceil(file.size / chunk_size)));

      const token = this.oidcSecurityService.getToken();
      const headers = { 'Authorization' : 'Bearer ' + token };

      fetch(url, { method: "POST", body: formData, headers })
        .then((value) => {
          const percentDone = Math.round(100 * (offset + chunk.size) / file.size);
          progress.next(percentDone);

          this.uploadExternalFileInternal(url, file, id, reference, blobalias, progress, chunk_number + 1, doneCallback, errorCallback);
        })
        .catch((reason) => {
          if (errorCallback) errorCallback(id, reason);
        });
    } else {
      if (doneCallback) { 
        progress.complete();
        doneCallback(id);
      }
    }
  }

  public download(url: string, params?: any): Promise<Blob> {
    const _params = new HttpParams({ fromObject: params, encoder: this.customUriEncoder });

    return this.http.get<Blob>(url, { headers: this.getHeaders(), responseType: 'blob' as 'json', params: _params }).toPromise();
  }

  public downloadExternalFile(url: string, reference: any, blobalias: any, params?: any): Promise<Blob> {
    var referenceDetail = reference ? ((typeof reference === 'function') ? reference() : reference ) : "";

    params = params || {};
    params.reference = referenceDetail;
    params.alias = blobalias;

    const _params = new HttpParams({ fromObject: params, encoder: this.customUriEncoder });

    return this.http.get<Blob>(url, { headers: this.getHeaders(), responseType: 'blob' as 'json', params: _params }).toPromise();
  }

  public deleteExternalFile(url: string, reference: any, blobalias: any, params?: any): Promise<any> {
    var referenceDetail = reference ? ((typeof reference === 'function') ? reference() : reference ) : "";

    params = params || {};
    params.reference = referenceDetail;
    params.alias = blobalias;

    const _params = new HttpParams({ fromObject: params, encoder: this.customUriEncoder });

    return this.handleObservable(this.http.delete<any>(url, { headers: this.getHeaders(), params: _params }));
  }

  private handleError(error: any): Promise<any> {
    return Promise.reject(error);
  }

  private handleObservable<T>(method: Observable<any>): Promise<T> {
    return method
        .toPromise()
        .then((response) => {
          if ((response as T)) {
            return response as T;
          }
        })
        .catch(e => this.handleError(e));
    }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts